import React, { useCallback, useContext, useEffect } from "react";
import {
  Button,
  Group,
  Space,
  Text,
  Textarea,
  TextInput,
  Loader,
  Breadcrumbs,
  Anchor,
  useMantineTheme,
  createStyles,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { useForm, yupResolver, formList, FormList } from "@mantine/form";
import { AxiosError } from "axios";
import { useQueryClient } from "react-query";
import { v4 as uuidv4 } from "uuid";
import { AuthContext } from "../../../providers/AuthProvider";
import { HttpClientError } from "../../../providers/HttpClient";
import useCreateSegmentApiService from "../services/api/CreateSegmentApiService";
import useAvailableFieldsApiService from "../services/api/AvailableFieldsApiService";
import RuleBuilder from "./RuleBuilder";
import RulePreview from "./RulePreview";
import SegmentRule from "../services/model/SegmentRule";
import SegmentFormValidationSchema from "../services/validator/SegmentFormValidationSchema";

const useStyles = createStyles(() => ({
  cancelButton: {
    borderColor: "#062133",
    color: "#062133",
    fontWeight: 600,
  },
  submitButton: {
    backgroundColor: "#062133",
    fontWeight: 600,
    "&:hover": {
      backgroundColor: "#0d3347",
    },
  },
}));

type SegmentCreateContentProps = {
  onBack: () => void;
  onSuccess: () => void;
};

const SegmentCreateContent: React.FC<SegmentCreateContentProps> = ({
  onBack,
  onSuccess,
}) => {
  const auth = useContext(AuthContext);
  const theme = useMantineTheme();
  const queryClient = useQueryClient();
  const { classes } = useStyles();

  const {
    isLoading: isCreateLoading,
    isSuccess: isCreateSuccess,
    isError: isCreateError,
    error: createError,
    mutate: onCreate,
  } = useCreateSegmentApiService();

  const { data: availableFields, isLoading: isFieldsLoading } =
    useAvailableFieldsApiService(
      { employer: auth?.employer.id ?? 0 },
      Boolean(auth?.employer?.id)
    );

  const form = useForm<{
    name: string;
    description: string;
    rules: FormList<SegmentRule>;
    logic_operator: "AND" | "OR";
    is_active: boolean;
  }>({
    validate: yupResolver(SegmentFormValidationSchema),
    initialValues: {
      name: "",
      description: "",
      rules: formList([{ id: uuidv4(), field: "", operator: "", value: "" }]),
      logic_operator: "OR",
      is_active: true,
    },
  });

  const failureNotification = useCallback(
    (err: AxiosError | null): void =>
      showNotification({
        title: "Segment Creation Failed",
        message:
          HttpClientError(err) || "Failed to create segment. Please try again.",
        color: "red",
        autoClose: 4000,
      }),
    []
  );

  useEffect(() => {
    if (isCreateError) {
      failureNotification(createError);
    }
  }, [isCreateError, createError, failureNotification]);

  useEffect(() => {
    if (isCreateSuccess) {
      queryClient.invalidateQueries(["SegmentListApiService"]).then(() => {
        showNotification({
          title: "Segment Created",
          message: "The segment has been created successfully.",
          color: "green",
          autoClose: 4000,
        });
        onSuccess();
      });
    }
  }, [isCreateSuccess, onSuccess, queryClient]);

  const onSubmitHandler = (formValues: typeof form.values): void => {
    if (!auth) return;
    onCreate({
      employer: auth.employer.id,
      ...formValues,
    });
  };

  if (isFieldsLoading) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", padding: "40px" }}
      >
        <Loader />
      </div>
    );
  }

  const breadcrumbItems = [
    { title: "Segmentation", onClick: onBack },
    { title: "Create new segment", onClick: undefined },
  ].map((item, index, arr) => (
    <Anchor
      key={item.title}
      onClick={(e) => {
        e.preventDefault();
        if (item.onClick) {
          item.onClick();
        }
      }}
      size="sm"
      style={{
        color: theme.black,
        opacity: index === arr.length - 1 ? 1 : 0.6,
        cursor: index === arr.length - 1 ? "default" : "pointer",
        textDecoration: "none",
      }}
    >
      {item.title}
    </Anchor>
  ));

  return (
    <>
      <Space h="md" />
      <Breadcrumbs separator=">">{breadcrumbItems}</Breadcrumbs>
      <Space h="xl" />

      <form
        onSubmit={form.onSubmit((formValues) => onSubmitHandler(formValues))}
      >
        <Text weight={600} size="lg" mb="md">
          Create a new segment
        </Text>

        <TextInput
          label="Segment name"
          placeholder="e.g Sales Team, Remote Workers etc..."
          required
          {...form.getInputProps("name")}
        />
        <Space h="md" />
        <Textarea
          label="Description"
          placeholder="Enter a description for this segment"
          required
          minRows={4}
          {...form.getInputProps("description")}
        />
        <Space h="xl" />

        <Text weight={600} size="md" mb="xs">
          Rules criteria
        </Text>
        <Text size="sm" color="dimmed" mb="md">
          Set up all your rules - add more information here if needed about how
          to set the rules
        </Text>

        <RuleBuilder form={form} availableFields={availableFields || []} />

        <Space h="xl" />
        <RulePreview
          rules={form.values.rules}
          logicOperator={form.values.logic_operator}
          employerId={auth?.employer.id ?? 0}
        />
        <Space h="xl" />

        <Group position="right">
          <Button
            variant="outline"
            radius={8}
            onClick={onBack}
            className={classes.cancelButton}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            radius={8}
            loading={isCreateLoading}
            classNames={{ root: classes.submitButton }}
          >
            Save Segment
          </Button>
        </Group>
      </form>
    </>
  );
};

export default SegmentCreateContent;
